import React, { useEffect, useState } from "react";
// NAVIGATION
import { useNavigate } from "react-router-dom";
// ANT DESIGN COMPONENTS
import { Table, Tag, Button } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faArrowsRotate } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import {
  useLazyGetBanksByOrganizationQuery,
  notificationAPI,
  useLazyGetBanksQuery,
} from "../../../redux/notifications/notificationAPI";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
// UTILS
import { getTableRowClass } from "../../../utils/utils";
// COMPONENTS
import TableNoDataBox from "../../components/table/TableNoDataBox";
// FILTERS
import FilterSearchText from "../../components/filters/FilterSearchText";
// INTERFACES
import { RootState } from "../../../app/store";
import { BankInterface } from "../../../interfaces/Bank.interface";

function NotificationBanksTable() {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const [t] = useTranslation("global");
  const { Column } = Table;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [banks, setBanks] = useState<BankInterface[]>([]);
  const [responseError, setResponseError] = useState<any>();
  const [filterName, setFilterName] = useState("");
  const [pagination, setPagination] = useState<any>({
    current: 1,
    pageSize: 10,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "50", "100"],
    total: 0,
  });

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { theme, globalOrganizationId } = useSelector((state: RootState) => state.home);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [
    triggerGetBanksByOrganization,
    {
      data: dataGetBanksByOrganization,
      isLoading: isLoadingGetBanksByOrganization,
      isError: isErrorGetBanksByOrganization,
      isFetching: isFetchingGetBanksByOrganization,
      error: errorGetBanksByOrganization,
    },
  ] = useLazyGetBanksByOrganizationQuery();

  const [
    triggerGetBanks,
    {
      data: dataGetBanks,
      isLoading: isLoadingGetBanks,
      isError: isErrorGetBanks,
      isFetching: isFetchingGetBanks,
      error: errorGetBanks,
    },
  ] = useLazyGetBanksQuery();

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const getData = async (page: number, limit: number) => {
    dispatch(notificationAPI.util.resetApiState()); // Resets the state for all queries in the API slice
    const token = await GETJwtToken();
    if (process.env.REACT_APP_MASTER_ORG_ID === globalOrganizationId) {
      const searchAndFilterString = `?page=${page}&limit=${limit}`;
      triggerGetBanks({
        token,
        searchAndFilterString,
      });
    } else {
      const searchAndFilterString = `?page=${page}&limit=${limit}&org_id=${globalOrganizationId}`;
      triggerGetBanksByOrganization({
        token,
        searchAndFilterString,
      });
    }
  };

  const onClickCell = (record: BankInterface) => {
    if (record) {
      navigate({
        pathname: "/notification-management/notifications",
        search: `?idBank=${record.id}&idOrganization=${
          record &&
          record.channel &&
          record.channel.server &&
          record.channel.server.org &&
          record.channel.server.org.id
            ? record.channel.server.org.id
            : "--"
        }`,
      });
    }
  };

  const handleTableChange = async (pagination_: any) => {
    setPagination({
      ...pagination,
      current: pagination_.current,
      pageSize: pagination_.pageSize,
      total: pagination_.total,
    });
    getData(pagination_.current, pagination_.pageSize);
  };

  const refreshData = () => {
    setFilterName("");
    setPagination({
      ...pagination,
      current: 1,
      pageSize: 10,
    });
    getData(1, 10);
  };

  // ************************************************* */
  // USE EFFECT ************************************** */
  useEffect(() => {
    if (globalOrganizationId && globalOrganizationId !== "") {
      getData(1, 10);
    }
  }, [globalOrganizationId]);

  useEffect(() => {
    const source = dataGetBanksByOrganization || dataGetBanks;
    if (source) {
      setBanks(source.data ?? []);
      setPagination({
        ...pagination,
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: source.total,
      });
    } else {
      setBanks([]);
    }
  }, [dataGetBanksByOrganization, dataGetBanks]);

  useEffect(() => {
    const error = errorGetBanksByOrganization || errorGetBanks;
    if (error) {
      setResponseError(error);
    }
  }, [errorGetBanksByOrganization, errorGetBanks]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div>
      <div className="generalStyles__mlFix mt-1">
        <Button
          className="buttonStyle__8"
          icon={<FontAwesomeIcon icon={faArrowsRotate} className="generalStyles__mrFix" />}
          onClick={() => {
            refreshData();
          }}
        >
          {t("edgeManagement.banks.refreshTable")}
        </Button>
      </div>
      <Table
        locale={{
          emptyText: (
            <TableNoDataBox
              isError={isErrorGetBanksByOrganization || isErrorGetBanks}
              errorMessage={
                responseError && responseError.data && responseError.data.message
                  ? responseError.data.message
                  : "Error"
              }
              noItemsFound="No data found"
            />
          ),
        }}
        loading={
          isLoadingGetBanksByOrganization ||
          isFetchingGetBanksByOrganization ||
          isLoadingGetBanks ||
          isFetchingGetBanks
        }
        rowClassName={(record, index) => getTableRowClass(index, theme)}
        dataSource={banks}
        size="small"
        className="my-custom-table"
        pagination={pagination}
        onChange={handleTableChange}
        rowKey={(record) => record.id}
        scroll={{ x: 900 }}
        footer={() => (
          <div className="generalStyles__flexEnd">
            <Tag>{pagination.total} Records</Tag>
          </div>
        )}
      >
        {/** NAME */}
        <Column
          onCell={(record: BankInterface) => ({
            onClick: () => {
              onClickCell(record);
            },
          })}
          className="generalStyles__hoverCursor"
          title={t("general.name")}
          dataIndex="name"
          key="name"
          filterIcon={<FontAwesomeIcon icon={faSearch} />}
          filterDropdown={() => (
            <FilterSearchText
              placeHolder={t("general.name")}
              filterValue={filterName}
              setFilterValue={setFilterName}
              refreshTable={() => {
                getData(1, 10);
              }}
              refreshRequireParamenters
            />
          )}
          sorter={(a: BankInterface, b: BankInterface) => a.name.localeCompare(b.name)}
          render={(text: string) => <div>{text}</div>}
        />
        {/** ORGANIZATION */}
        <Column
          onCell={(record: BankInterface) => ({
            onClick: () => {
              onClickCell(record);
            },
          })}
          className="generalStyles__hoverCursor"
          title={t("general.organization")}
          dataIndex="organization"
          key="organization"
          render={(text, record) => (
            <div>
              {record &&
              record.channel &&
              record.channel.server &&
              record.channel.server.org &&
              record.channel.server.org.name
                ? record.channel.server.org.name
                : "--"}
            </div>
          )}
        />
        {/** TYPE -- */}
        <Column
          onCell={(record: BankInterface) => ({
            onClick: () => {
              onClickCell(record);
            },
          })}
          className="generalStyles__hoverCursor"
          title={t("edgeManagement.lifts.type")}
          dataIndex="type"
          key="type"
          width="100px"
          render={(text) => (
            <>
              <div>
                {text === "ELEVATOR" && (
                  <Tag color="blue" className="drawer__typeTag">
                    {text}
                  </Tag>
                )}
                {text === "ESCALATOR" && (
                  <Tag color="volcano" className="drawer__typeTag">
                    {text}
                  </Tag>
                )}
                {text === "MOVINGWALK" && (
                  <Tag color="green" className="drawer__typeTag">
                    {text}
                  </Tag>
                )}
                {text === "BOT" && (
                  <Tag color="magenta" className="drawer__typeTag">
                    {text}
                  </Tag>
                )}
                {text === "GPIO" && (
                  <Tag color="cyan" className="drawer__typeTag">
                    {text}
                  </Tag>
                )}
              </div>
            </>
          )}
        />
      </Table>
    </div>
  );
}

export default NotificationBanksTable;
